import { type Association, type Organization, type Status } from "@entities";
import { type ColumnProps, type HyperTableDef } from "@shared/frontend/hyper-table/types";

import { MenuItem, Select, Stack, Typography } from "@mui/material";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { format } from "date-fns";
import dayjs from "dayjs";
import { associationRepo } from "@repositories";
import { useOrganization } from "clients/admin/src/desktop/contexts/OrganizationProvider";
import PhoneNumberCell from "../../PhoneNumberCell";
import ActivityStatus from "./components/ActivityStatus";
import Insurance from "./components/Insurance";
import Skills from "./components/Skills";
import UserNameAndPhoto from "./components/UserNameAndPhoto";
import { useAlert } from "clients/admin/src/desktop/contexts/AlertProvider";
import { CommunicationRecordsTableCell } from "clients/admin/src/desktop/components/CommunicationRecordManager/CommunicationRecordsTableCell";
import { TextPreviewPopover } from "clients/admin/src/desktop/components/text-preview/TextPreviewPopover";

function formatVolunteerAge(dateOfBirth: Date | string | null | undefined): string {
  if (!dateOfBirth) return "-";
  const dob = dayjs(dateOfBirth);
  if (!dob.isValid()) return "-";
  const age = dayjs().diff(dob, "year");
  if (age < 0 || age > 120) return "-";
  return String(age);
}

export const useVolunteerColumns = (
  currOrganization: Organization | undefined,
  hyperTable: HyperTableDef<Association>,
  groupId?: string,
  onCallInitiated?: (associationId: string) => void,
): ColumnProps<Association>[] => {
  const { setAlertText, setSeverity } = useAlert();
  return [
      {
        field: "user.name",
        cell: ({ row }) => <UserNameAndPhoto strongName={groupId ? row.original.personalDetails?.name : undefined} user={row.original.user} />,
        minSize: 150,
        size: 180,
      },
      { field: "activityStatus", cell: ActivityStatus },
      {
        field: "user.phoneNumber",
        minSize: 150,
        size: 160,
        cell: ({ row }) => (
          <PhoneNumberCell
            phoneNumber={row.original.user?.phoneNumber || ""}
            onCallInitiated={() => onCallInitiated?.(row.original.id)}
          />
        ),
        preventRowClick: true,
      },
      {
        field: "communicationRecords",
        caption: "תיעוד תקשורת",
        cell: ({ row }) => (
          <CommunicationRecordsTableCell
            records={row.original.communicationRecords}
            associationId={row.original.id}
            title={row.original.user?.name ?? "מתנדב"}
            onSave={() => hyperTable.context.current?.refetch("invalidate")}
          />
        ),
        preventRowClick: true,
      },
      {
        field: "user.dateOfBirth",
        caption: "גיל",
        minSize: 70,
        size: 80,
        cell: ({ row }) => formatVolunteerAge(row.original.user?.dateOfBirth),
      },
      {
        field: "user.email",
        caption: "אימייל",
        minSize: 140,
        size: 170,
        cell: ({ row }) => row.original.user?.email?.trim() || "-",
      },
      { field: "user.address", minSize: 160, size: 175 },
      { field: "user.gender", minSize: 80, size: 100 },
      {
        field: "user.hasVehicle",
        caption: "רכב",
        minSize: 70,
        size: 80,
        cell: ({ row }) => (row.original.user?.hasVehicle ? "כן" : "לא"),
      },
      {
        field: "status",
        cell: ({ row }) => {
          const { currOrganization } = useOrganization();
          return (
            <Stack
              alignItems="center"
              flexDirection="column"
              gap={0.25}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <Select
                className="status-select-groups"
                labelId="select-label"
                value={row.original.status?.title || "ללא סטטוס"}
                sx={{
                  backgroundColor: row.original.status?.color,
                  color: row.original.status?.color ? "white" : "black",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                  maxWidth: "100%",
                  /* eslint-disable @typescript-eslint/naming-convention -- MUI sx */
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  /* eslint-enable @typescript-eslint/naming-convention -- MUI sx */
                }}
                IconComponent={undefined}
              >
                <MenuItem
                  sx={{
                    backgroundColor: "white",
                    ["&:hover"]: {
                      backgroundColor: "#f0f0f0",
                    },
                    color: "black",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                  }}
                  value="ללא סטטוס"
                  onClick={async () => {
                    await associationRepo.update(row.original.id, {
                      statusId: "",
                      statusUpdatedAt: new Date(),
                    });
                    setAlertText("הסטטוס עודכן בהצלחה");
                    setSeverity("success");
                    hyperTable.context.current?.refetch("invalidate");
                  }}
                >
                  ללא סטטוס
                </MenuItem>
                {currOrganization?.status
                  ?.filter((status) => status.type === "VOLUNTEER")
                  .sort((a, b) => a.order - b.order)
                  ?.map((status: Status) => (
                    <MenuItem
                      title={status.title}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        backgroundColor: `${status.color} !important`,
                        /* eslint-disable @typescript-eslint/naming-convention -- MUI sx*/
                        "&:hover": {
                          backgroundColor: `${status.color}CC`,
                        },
                        "&.Mui-selected": {
                          backgroundColor: `${status.color}CC`,
                          border: "3.5px solid white",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: `${status.color}CC`,
                        },
                        /* eslint-enable @typescript-eslint/naming-convention -- MUI sx*/
                        color: "white",
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center",
                      }}
                      key={status.id}
                      value={status.title}
                      onClick={async () => {
                        await associationRepo.update(row.original.id, {
                          statusId: status.id,
                          statusUpdatedAt: new Date(),
                        });
                        hyperTable.context.current?.refetch("invalidate");
                      }}
                    >
                      {status.title}
                    </MenuItem>
                  ))}
              </Select>
              {row.original.statusUpdatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {format(new Date(row.original.statusUpdatedAt), "dd/MM/yyyy HH:mm")}
                </Typography>
              )}
            </Stack>
          );
        },
        preventRowClick: true,
        minSize: 200,
      },
      {
        field: "teams.team.name",
        caption: "פרויקטים",
        minSize: 150,
        size: 180,
        valueConverter: (value) => {
          if (!value) return "-";
          return value.teams
            ?.map((team) => team.team?.name)
            ?.filter(team => !!team)
            ?.join(", ");
        },
      },
      {
        field: "user.skills.skill.name",
        cell: Skills,
        caption: "יכולות",
        minSize: 150,
        size: 180,
      },
      { field: "user.numberID", caption: "מידע לביטוח", cell: Insurance, minSize: 100, size: 100 },
      {
        field: "preferredPositions",
        caption: "העדפות",
        minSize: 100,
        size: 150,
        valueConverter: (value) => {
          if (!value.user) return "";
          return value?.preferredPositions
            ?.map((position) => position)
            .join(", ");
        },
      },
      {
        field: "comments",
        caption: "הערות לשימוש פנימי",
        cell: ({ row }) => (
          <TextPreviewPopover
            text={row.original.comments ?? ""}
            icon={<StickyNote2OutlinedIcon fontSize="small" />}
          />
        ),
        preventRowClick: true,
        minSize: 120,
        size: 150,
      },
      {
        field: "scholarshipsHoursNumber",
        caption: "שעות מלגה",
        minSize: 100,
        size: 120,
      },
      {
        field: "createdAt",
        caption: "נוצר",
        minSize: 110,
        size: 120,
        cell: ({ row }) =>
          row.original.createdAt
            ? dayjs(row.original.createdAt).format("DD/MM/YYYY")
            : "-",
      },
    ];
};

export default useVolunteerColumns;
